// ============================================================
// FRONTEND: src/features/reports/services/reportsApi.ts (ENHANCED)
// ============================================================

import apiClient from '@/lib/api-client';

// ==================== TYPES ====================

export interface ReportParams {
    startDate?: string;
    endDate?: string;
    branchId?: string;
    groupBy?: string;
    limit?: number;
    productId?: string;
    customerId?: string;
}

export interface ComparisonData {
    revenue: number;
    orders: number;
    revenueChange: string;
    ordersChange: string;
}

// Sales
export interface SalesSummaryData {
    summary: {
        totalSales: number;
        totalRevenue: string;
        averageOrderValue: string;
        totalVoided: string;
        totalRefunded: string;
        voidCount: number;
        refundCount: number;
        totalTax: string;
        totalDiscount: string;
        totalCost: string;
        grossProfit: string;
        profitMargin: string;
    };
    paymentBreakdown: Array<{ method: string; amount: string; percentage: string }>;
    hourlyBreakdown: Array<{ hour: number; revenue: number; count: number }>;
    dailyBreakdown: Array<{ date: string; revenue: number; count: number; tax: number; discount: number; cost: number }>;
    comparison: ComparisonData | null;
    recentSales: Array<{ id: string; orderNumber: string; grandTotal: string; cashierName: string; createdAt: string }>;
}

// ==================== API FUNCTIONS ====================

export async function getSalesSummary(tenantId: string, params: ReportParams): Promise<SalesSummaryData> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/sales/summary`, { params });
    return data?.data;
}

export async function getSalesByProduct(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/sales/by-product`, { params });
    return data?.data;
}

export async function getSalesByCategory(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/sales/by-category`, { params });
    return data?.data;
}

export async function getSalesByCashier(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/sales/by-cashier`, { params });
    return data?.data;
}

export async function getSalesByPaymentMethod(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/sales/by-payment-method`, { params });
    return data?.data;
}

export async function getVoidRefundReport(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/sales/void-refund`, { params });
    return data?.data;
}

// Inventory
export async function getStockValuation(tenantId: string, branchId?: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/inventory/valuation`, { params: { branchId } });
    return data?.data;
}

export async function getStockMovement(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/inventory/movement`, { params });
    return data?.data;
}

export async function getLowStockReport(tenantId: string, branchId?: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/inventory/low-stock`, { params: { branchId } });
    return data?.data;
}

export async function getStockAgingReport(tenantId: string, branchId?: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/inventory/aging`, { params: { branchId } });
    return data?.data;
}

// Customers
export async function getTopCustomers(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/customers/top`, { params });
    return data?.data;
}

export async function getCustomerPurchaseHistory(tenantId: string, params: ReportParams & { customerId?: string; page?: number }) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/customers/purchase-history`, { params });
    return data?.data;
}

export async function getLoyaltySummary(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/customers/loyalty`);
    return data?.data;
}

export async function getCustomerDebtReport(tenantId: string) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/customers/debt`);
    return data?.data;
}

// Staff
export async function getStaffPerformanceReport(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/staff/performance`, { params });
    return data?.data;
}

export async function getShiftSummaryReport(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/staff/shifts`, { params });
    return data?.data;
}

// Products
export async function getTopSellingProducts(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/products/top-selling`, { params });
    return data?.data;
}

export async function getSlowMovingProducts(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/products/slow-moving`, { params });
    return data?.data;
}

export async function getProfitMarginReport(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/products/profit-margin`, { params });
    return data?.data;
}

// Financial
export async function getTaxReport(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/finance/tax`, { params });
    return data?.data;
}

export async function getDiscountSummary(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/finance/discounts`, { params });
    return data?.data;
}

export async function getDailyRevenue(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/finance/daily-revenue`, { params });
    return data?.data;
}

export async function getProfitLossReport(tenantId: string, params: ReportParams) {
    const { data } = await apiClient.get(`/tenants/${tenantId}/reports/finance/p-and-l`, { params });
    return data?.data;
}

export function getExportUrl(tenantId: string, type: string, format: 'pdf' | 'excel', params: Record<string, any>) {
    const cleanParams: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== '') {
            cleanParams[key] = String(value);
        }
    }
    const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') || '' : '';
    const searchParams = new URLSearchParams({ type, format, ...cleanParams });
    if (token) {
        searchParams.set('token', token);
    }
    return `/api/v1/tenants/${tenantId}/reports/export?${searchParams.toString()}`;
}

export async function downloadReportFile(
    tenantId: string,
    type: string,
    format: 'pdf' | 'excel',
    params: Record<string, any>,
    customFilename?: string
): Promise<void> {
    const cleanParams: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== '') {
            cleanParams[key] = String(value);
        }
    }

    try {
        const response = await apiClient.get(`/tenants/${tenantId}/reports/export`, {
            params: { type, format, ...cleanParams },
            responseType: 'blob',
        });

        const extension = format === 'pdf' ? 'pdf' : 'xlsx';
        const dateStr = new Date().toISOString().slice(0, 10);
        const filename = customFilename || `${type}_report_${dateStr}.${extension}`;

        const blob = new Blob([response.data], {
            type: format === 'pdf'
                ? 'application/pdf'
                : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    } catch (error) {
        // Fallback to window.open with query token
        const url = getExportUrl(tenantId, type, format, params);
        window.open(url, '_blank');
    }
}